import { handleApiErr, useAPI } from "@/lib/api";
import {
  StarIcon,
  StarIcon as StarIconOutline,
} from "@heroicons/react/24/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/24/solid";
import { Form, Formik } from "formik";
import { CheckIcon, StoreIcon } from "lucide-react";
import { Fragment, useMemo } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import * as Yup from "yup";
import Button, { FormikSubmit } from "../Button";
import ErrorField from "../ErrorField";
import { FieldTextarea, InputLabel } from "../Input";
import ModalComponent, { ModalHeader, useModal } from "../Modal";
import { FieldSwitchButton } from "../SwitchButton";
import { Skeleton } from "../ui/skeleton";

export default function ReviewForm({ itemId, hashId, fullName }) {
  const reviewModal = useModal();
  const previewModal = useModal();

  const { data, isLoading, mutate } = useSWR(
    `/microsite/shop/review/${itemId}?` +
      new URLSearchParams({
        hash: hashId,
      }),
    {
      errorRetryCount: 0,
      revalidateOnFocus: true,
      shouldRetryOnError: false,
    }
  );

  const canReview = data?.data?.can_review;

  const reviewed = useMemo(() => {
    if (data?.data) {
      return data?.data?.rating;
    }
    return false;
  }, [data]);

  return (
    <Fragment>
      {isLoading ? (
        <Skeleton className="mt-1.5 w-24 h-6" />
      ) : !canReview ? (
        <></>
      ) : reviewed ? (
        <Button
          type="button"
          onClick={() => {
            previewModal.open();
          }}
          variant="ghost"
          size="sm"
          className="flex items-center gap-1 mt-1 text-muted-foreground text-xs"
        >
          <CheckIcon className="size-3" /> Has been reviewed
        </Button>
      ) : (
        <Button
          type="button"
          onClick={() => {
            reviewModal.open();
          }}
          variant="secondary"
          size="sm"
          className="flex items-center gap-1 mt-1 text-xs"
        >
          <StarIcon className="size-3" /> Leave a review
        </Button>
      )}
      <ModalComponent {...reviewModal} className="md:!max-w-lg" drawer>
        <ModalHeader {...reviewModal}>Leave a review</ModalHeader>
        <ReviewModal
          data={data?.data}
          itemId={itemId}
          hashId={hashId}
          onClose={() => {
            reviewModal.close();
            previewModal.open();
          }}
          mutate={mutate}
          fullName={fullName}
        />
      </ModalComponent>

      <ModalComponent {...previewModal} className="md:!max-w-lg" drawer>
        <ModalHeader {...previewModal}>Your Review</ModalHeader>
        <PreviewModal data={data?.data} />
      </ModalComponent>
    </Fragment>
  );
}

function PreviewModal({ data }) {
  return (
    <div className="flex flex-col gap-1 pb-10 md:pb-0">
      <div>
        <h2 className="font-bold">{data?.rating?.full_name}</h2>
      </div>
      <div className="flex gap-1">
        {Array.from({ length: data?.rating?.rating }).map((_, index) => (
          <StarIconSolid key={index} className="w-4 text-yellow-400" />
        ))}
      </div>
      <div className="pt-1">
        <p className="text-sm">{data?.rating?.comment}</p>
      </div>

      <div className="mt-2 pt-2 border-t">
        <h2 className="flex items-center font-bold text-sm">
          <StoreIcon className="mr-1 size-4" /> {data?.shop_name}
        </h2>
        <p className="text-muted-foreground text-sm">{data?.product_name}</p>
      </div>
    </div>
  );
}

function ReviewModal({
  data,
  itemId,
  hashId,
  onClose,
  mutate,
  fullName,
}: {
  data: any;
  itemId: string;
  hashId: string;
  onClose: () => void;
  mutate: () => void;
  fullName: string;
}) {
  const api = useAPI();
  const { t } = useTranslation();

  const onSubmit = async (values: any, { setErrors }) => {
    try {
      await api.post(
        `/microsite/shop/review/${itemId}?` +
          new URLSearchParams({ hash: hashId }),
        values
      );
      toast.success("Review submitted successfully");
      await mutate();
      onClose();
    } catch (e) {
      handleApiErr(e, { toast, t, setErrors });
    }
  };

  const validationSchema = Yup.object({
    comment: Yup.string().required().min(10).max(255),
    rating: Yup.number().required().min(1).max(5),
  });

  return (
    <Formik
      initialValues={{ comment: "", rating: 0, mask_name: false }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ values, setFieldValue }) => (
        <Form className="flex flex-col gap-3 mt-5">
          <div>
            <h2 className="flex items-center font-bold text-sm">
              <StoreIcon className="mr-1 size-4" /> {data.shop_name}
            </h2>
            <p className="text-muted-foreground text-sm">{data.product_name}</p>
          </div>
          <div className="text-muted-foreground text-sm">
            Comment as: <strong>{fullName}</strong>
          </div>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((item) => (
              <button
                key={item}
                type="button"
                className="flex justify-center items-center text-black"
                onClick={() => {
                  setFieldValue("rating", item);
                }}
              >
                {item <= values.rating ? (
                  <StarIconSolid className="w-6 text-yellow-400" />
                ) : (
                  <StarIconOutline className="w-6 text-muted-foreground/50" />
                )}
              </button>
            ))}
          </div>
          <ErrorField name="rating" />
          <div>
            <InputLabel>Comment</InputLabel>
            <FieldTextarea
              name="comment"
              className="w-full min-h-12"
              placeholder="Write your review here"
              minLength={10}
            />
            <ErrorField name="comment" />
          </div>

          <div>
            <InputLabel className="flex items-center gap-2 !font-normal !text-muted-foreground text-sm">
              <FieldSwitchButton name="mask_name" /> Mask your Name
            </InputLabel>
          </div>
          <div className="pb-10 md:pb-0">
            <FormikSubmit className="w-full" variant="dark">
              Submit
            </FormikSubmit>
          </div>
        </Form>
      )}
    </Formik>
  );
}
